import { EditOutlined, LoadingOutlined } from '@ant-design/icons';
import { DocumentShared, SharedSpinner } from '@components';
import { RequestDto } from '@services';
import { Col, Divider, Pagination, Row, Typography } from 'antd';
import dayjs from 'dayjs';
import React from 'react';
import { useView } from '@hooks';
import DocumentDropZone from './DocumentDropZone';
import OrderNoteForm from './OrderNoteForm';
import { IOrderNotesProps } from './OrderNotes.props';

const { Text } = Typography;

declare const abp: any;

const OrderNotes: React.FC<IOrderNotesProps> = ({
  form,
  uploading,
  orderNotesHook,
  orderDocumentsHook,
  request,
  requestLoading,
  handleUpload,
  handleCreateOrUpdateNote,
  handleDocumentDelete,
}) => {
  const { isMobile } = useView();
  const { documents, images, videos } = orderDocumentsHook;
  const { dataSource: orderNotes, totalCount } = orderNotesHook;

  const groupedConcerns =
    request?.concerns?.reduce(
      (acc, concern) => {
        const category = concern.categoryName;
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(concern);
        return acc;
      },
      {} as Record<string, RequestDto['concerns']>
    ) || {};

  // Customer Request Section
  const customerRequestSection = request && (
    <>
      <Row>
        <Col>
          <Text strong>Customer request note</Text>
        </Col>
      </Row>
      <Row>
        <Col>
          <br />
          {Object.entries(groupedConcerns).map(([category, concerns]) => (
            <div key={category} style={{ marginBottom: '16px' }}>
              <Text strong>{category}</Text>
              <ul>
                {concerns.map((concern) => (
                  <li key={concern.id}>
                    <Text>{concern.name}</Text>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </Col>
      </Row>
      <Row>
        <Col>
          <Text>{request?.note}</Text>
        </Col>
      </Row>
      <Row>
        <Col>
          <br />
          {request?.documents?.length ? (
            request?.documents.map((document) => (
              <DocumentShared hideDelete key={document.id} document={document} />
            ))
          ) : (
            <></>
          )}
        </Col>
      </Row>
    </>
  );

  // Upload Section
  const uploadSection = (
    <>
      <Row>
        <Col span={24}>
          <Divider titlePlacement="left" orientationMargin="0">
            Upload files
          </Divider>
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          {(uploading && <LoadingOutlined style={{ fontSize: '50px' }} />) || (
            <DocumentDropZone handleUpload={handleUpload} />
          )}
        </Col>
      </Row>
    </>
  );

  // Documents Section
  const documentsSection = (
    <>
      <Row>
        <Col span={24}>
          <Divider titlePlacement="center" style={{display: 'block !important'}}>Documents</Divider>
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          {documents.map((document) => (
            <DocumentShared
              key={document.id}
              document={document}
              handleDocumentDelete={handleDocumentDelete}
              fontSize={isMobile ? 20 : 40}
            />
          ))}
        </Col>
      </Row>
    </>
  );

  // Images Section
  const imagesSection = (
    <>
      <Row>
        <Col span={24}>
          <Divider titlePlacement="center" orientationMargin="0">
            Images
          </Divider>
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          {images.map((document) => (
            <DocumentShared
              key={document.id}
              document={document}
              handleDocumentDelete={handleDocumentDelete}
              imageWidth={isMobile ? 64 : 40}
              imageHeight={isMobile ? 64 : 40}
            />
          ))}
        </Col>
      </Row>
    </>
  );

  // Videos Section
  const videosSection = (
    <>
      <Row>
        <Col span={24}>
          <Divider titlePlacement="center" orientationMargin="0">
            Videos
          </Divider>
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          {videos.map((document) => (
            <DocumentShared
              key={document.id}
              document={document}
              handleDocumentDelete={handleDocumentDelete}
            />
          ))}
        </Col>
      </Row>
    </>
  );

  // Note Form Section
  const noteFormSection = (
    <>
      <Row style={{ paddingBottom: '40px' }}>
        <Col span={24}>
          <OrderNoteForm form={form} handleSubmit={handleCreateOrUpdateNote} />
        </Col>
      </Row>

      {orderNotesHook.loading && (
        <Row justify="center">
          <Col>
            <SharedSpinner />
          </Col>
        </Row>
      )}
      {!orderNotesHook.loading &&
        orderNotes?.map((note) => (
          <Row key={note.id} style={{ paddingBottom: '20px' }}>
            <Col span={24}>
              <Text strong>
                {note.creatorUser.fullname} - {dayjs(note.creationTime).format('DD/MM/YYYY')}
              </Text>
              {note.lastModificationTime && (
                <Text type="secondary" style={{ marginLeft: '8px' }}>
                  Edited - {dayjs(note.lastModificationTime).format('DD/MM/YYYY')}
                </Text>
              )}
            </Col>
            <Col span={24} style={{ display: 'flex', alignItems: 'center' }}>
              <Text style={{ flex: 1 }}>{note.description}</Text>
              {abp.session.userId === note.creatorUserId && (
                <EditOutlined
                  style={{ marginLeft: '8px', cursor: 'pointer' }}
                  onClick={() => {
                    form.setFieldsValue({
                      id: note.id,
                      orderId: note.orderId,
                      description: note.description,
                    });
                  }}
                />
              )}
            </Col>
          </Row>
        ))}
      {totalCount > orderNotesHook.maxResultCount && (
        <Row justify="center">
          <Col>
            <Pagination
              current={orderNotesHook.currentPage}
              pageSize={orderNotesHook.maxResultCount}
              total={orderNotesHook.totalCount}
              onChange={orderNotesHook.handlePageChange}
            />
          </Col>
        </Row>
      )}
    </>
  );

  // Mobile Layout - Single column layout
  if (isMobile) {
    return (
      <Row gutter={[0, 24]} style={{ paddingBottom: '40px' }}>
        <Col span={24}>{noteFormSection}</Col>
        <Col span={24}>{requestLoading ? <SharedSpinner /> : customerRequestSection}</Col>
        <Col span={24}>{uploadSection}</Col>
        <Col span={24}>{documentsSection}</Col>
        <Col span={24}>{imagesSection}</Col>
        <Col span={24}>{videosSection}</Col>
      </Row>
    );
  }

  // Desktop Layout - Original two-column layout
  return (
    <Row gutter={48}>
      <Col span={12}>{noteFormSection}</Col>
      {requestLoading ? (
        <SharedSpinner />
      ) : (
        <Col span={12}>
          {customerRequestSection}
          {uploadSection}
          {documentsSection}
          {imagesSection}
          {videosSection}
        </Col>
      )}
    </Row>
  );
};

export default OrderNotes;
